import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import type { Ambito, Ente } from "./tipos"

export function Pie({
  error,
  pendiente,
  listo,
  onCerrar,
  onGuardar,
}: {
  error: unknown
  pendiente: boolean
  listo: boolean
  onCerrar: () => void
  onGuardar: () => void
}) {
  return (
    <>
      {error ? (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo guardar"}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton variante="primario" disabled={!listo || pendiente} onClick={onGuardar}>
          Guardar
        </Boton>
      </div>
    </>
  )
}

/** Crear o editar un ente. Un ente no se borra: se desactiva. */
export function EditorEnte({ ente, onCerrar }: { ente: Ente | null; onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const [d, setD] = useState({
    codigo: ente?.codigo ?? "",
    nombre: ente?.nombre ?? "",
    ambito: ente?.ambito ?? ("NACIONAL" as Ambito),
    sitio_web: ente?.sitio_web ?? "",
    notas: ente?.notas ?? "",
    activo: ente?.activo ?? true,
  })
  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = { ...d, sitio_web: d.sitio_web.trim() || null, notas: d.notas.trim() || null }
      return ente
        ? api.put<Ente>(`/api/v1/cumplimiento/entes/${ente.id}`, cuerpo)
        : api.post<Ente>("/api/v1/cumplimiento/entes", cuerpo)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["cumplimiento"] })
      onCerrar()
    },
    meta: { exito: "Ente guardado" },
  })
  return (
    <Modal
      abierto={true}
      titulo={ente ? `Editar ${ente.codigo}` : "Nuevo ente"}
      onCerrar={onCerrar}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
        <div>
          <label htmlFor="ente-codigo" className="mb-1 block text-xs font-medium uppercase">
            Código
          </label>
          <input
            id="ente-codigo"
            className="campo font-mono"
            value={d.codigo}
            onChange={(e) => setD({ ...d, codigo: e.target.value.toUpperCase() })}
          />
        </div>
        <div>
          <label htmlFor="ente-nombre" className="mb-1 block text-xs font-medium uppercase">
            Nombre
          </label>
          <input
            id="ente-nombre"
            className="campo"
            value={d.nombre}
            onChange={(e) => setD({ ...d, nombre: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="ente-ambito" className="mb-1 block text-xs font-medium uppercase">
            Ámbito
          </label>
          <select
            id="ente-ambito"
            className="campo"
            value={d.ambito}
            onChange={(e) => setD({ ...d, ambito: e.target.value as Ambito })}
          >
            <option value="NACIONAL">Nacional</option>
            <option value="ESTADAL">Estadal</option>
            <option value="MUNICIPAL">Municipal</option>
          </select>
        </div>
        <div>
          <label htmlFor="ente-web" className="mb-1 block text-xs font-medium uppercase">
            Sitio web
          </label>
          <input
            id="ente-web"
            className="campo"
            value={d.sitio_web}
            onChange={(e) => setD({ ...d, sitio_web: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ente-notas" className="mb-1 block text-xs font-medium uppercase">
            Notas
          </label>
          <textarea
            id="ente-notas"
            rows={2}
            className="campo"
            value={d.notas}
            onChange={(e) => setD({ ...d, notas: e.target.value })}
          />
        </div>
        {ente && (
          <label className="flex items-center gap-1.5 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={d.activo}
              onChange={(e) => setD({ ...d, activo: e.target.checked })}
            />
            Activo
          </label>
        )}
      </div>
      <Pie
        error={guardar.error}
        pendiente={guardar.isPending}
        listo={d.codigo.trim().length >= 2 && d.nombre.trim().length >= 2}
        onCerrar={onCerrar}
        onGuardar={() => guardar.mutate()}
      />
    </Modal>
  )
}
